// ═══════════════════════════════════════════════════════════════════════════
// useImpuestosEmpresa — Hook que resuelve el config fiscal completo de una empresa
// ═══════════════════════════════════════════════════════════════════════════
//
// Hace el merge de 3 fuentes:
//   1. catalogoImpuestos/{jurisdiccionId}        — defaults plataforma (21%, 4%, etc.)
//   2. catalogoCondicionesFiscales/{condId}      — flags por tipo de seller (RI/Mono/Exento)
//   3. empresasImpuestos/{empresaId}             — override por empresa (IIBB distinto, etc.)
//
// Devuelve un objeto config completo SIN huecos:
//   {
//     loading, error,
//     jurisdiccionId, condicionFiscalId,
//     ivaGeneral, ivaReducido, iibb, impuestoCredDebito, factorGanancias,
//     flags: { discriminaIVA, pagaIIBB, pagaImpCredDebito, aplicaGananciasEstimado },
//     fuentes: {                          ← de dónde salió cada peso
//       ivaGeneral: 'plataforma' | 'empresa',
//       iibb: ...,
//       ...
//     },
//     overrideEmpresaDoc: { ... }         ← raw del doc para el ABM
//   }
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react'
import { doc, onSnapshot, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { docConId } from '@core/lib/docConId'
import { COLLECTIONS } from '@core/lib/constants'

const JURISDICCION_DEFAULT = 'AR'

const VALORES_CONOCIDOS = ['ivaGeneral', 'ivaReducido', 'iibb', 'impuestoCredDebito', 'factorGanancias']

export function useImpuestosEmpresa(empresaId, jurisdiccionId = JURISDICCION_DEFAULT) {
  const [overrideEmpresa, setOverrideEmpresa] = useState(null)
  const [catalogoPlataforma, setCatalogoPlataforma] = useState(null)
  const [condicionFiscal, setCondicionFiscal] = useState(null)
  const [loadingEmpresa, setLoadingEmpresa] = useState(true)
  const [loadingCatalogo, setLoadingCatalogo] = useState(true)
  const [error, setError] = useState(null)

  // 1) Override de la empresa (escucha en vivo)
  useEffect(() => {
    if (!empresaId) { setOverrideEmpresa(null); setLoadingEmpresa(false); return }
    setLoadingEmpresa(true)
    const ref = doc(db, COLLECTIONS.EMPRESAS_IMPUESTOS, empresaId)
    const unsub = onSnapshot(ref, (snap) => {
      setOverrideEmpresa(snap.exists() ? docConId(snap) : null)
      setLoadingEmpresa(false)
    }, (err) => {
      console.error('[useImpuestosEmpresa] override:', err)
      setError(err)
      setLoadingEmpresa(false)
    })
    return () => unsub()
  }, [empresaId])

  // 2) Catálogo plataforma (jurisdicción)
  useEffect(() => {
    setLoadingCatalogo(true)
    const ref = doc(db, COLLECTIONS.CATALOGO_IMPUESTOS, jurisdiccionId)
    const unsub = onSnapshot(ref, (snap) => {
      setCatalogoPlataforma(snap.exists() ? docConId(snap) : null)
      setLoadingCatalogo(false)
    }, (err) => {
      console.error('[useImpuestosEmpresa] catalogo:', err)
      setError(err)
      setLoadingCatalogo(false)
    })
    return () => unsub()
  }, [jurisdiccionId])

  // 3) Condición fiscal (depende del override de la empresa)
  useEffect(() => {
    const condId = overrideEmpresa?.condicionFiscalId
    if (!condId) { setCondicionFiscal(null); return }
    let cancelled = false
    getDoc(doc(db, COLLECTIONS.CATALOGO_CONDICIONES_FISCALES, condId))
      .then(snap => {
        if (!cancelled) setCondicionFiscal(snap.exists() ? docConId(snap) : null)
      })
      .catch(err => console.error('[useImpuestosEmpresa] condicion:', err))
    return () => { cancelled = true }
  }, [overrideEmpresa?.condicionFiscalId])

  // ─── Merge ───
  const loading = loadingEmpresa || loadingCatalogo
  const config = mergearConfig({ catalogoPlataforma, overrideEmpresa, condicionFiscal, jurisdiccionId })

  return { ...config, loading, error, overrideEmpresaDoc: overrideEmpresa, catalogoPlataformaDoc: catalogoPlataforma, condicionFiscalDoc: condicionFiscal }
}

// Helper puro: merge de plataforma + override empresa + flags de condición fiscal.
// Exportado por si algún componente quiere mergear sus propios docs sin el hook.
export function mergearConfig({ catalogoPlataforma, overrideEmpresa, condicionFiscal, jurisdiccionId = JURISDICCION_DEFAULT }) {
  const fuentes = {}
  const out = {
    jurisdiccionId,
    condicionFiscalId: overrideEmpresa?.condicionFiscalId || null,
    notas: overrideEmpresa?.notas || null,
  }
  for (const key of VALORES_CONOCIDOS) {
    const overridable = overrideEmpresa?.[key]
    const plataforma = catalogoPlataforma?.[key]
    if (overridable != null) {
      out[key] = overridable
      fuentes[key] = 'empresa'
    } else if (plataforma != null) {
      out[key] = plataforma
      fuentes[key] = 'plataforma'
    } else {
      out[key] = null
      fuentes[key] = null
    }
  }
  out.flags = condicionFiscal?.flags || {
    discriminaIVA:           true,   // default RI
    pagaIIBB:                true,
    pagaImpCredDebito:       true,
    aplicaGananciasEstimado: true,
  }
  out.condicionFiscalNombre = condicionFiscal?.nombre || null
  out.fuentes = fuentes
  return out
}

// ─── Helpers de ABM ──────────────────────────────────────────────────────

export async function guardarImpuestosEmpresa(empresaId, payload, userId) {
  if (!empresaId) throw new Error('empresaId requerido')
  const ref = doc(db, COLLECTIONS.EMPRESAS_IMPUESTOS, empresaId)
  await setDoc(ref, {
    empresaId,
    jurisdiccionId:   payload.jurisdiccionId || JURISDICCION_DEFAULT,
    condicionFiscalId: payload.condicionFiscalId || null,
    // Overrides — null si quiere usar el default plataforma
    ivaGeneral:         payload.ivaGeneral ?? null,
    ivaReducido:        payload.ivaReducido ?? null,
    iibb:               payload.iibb ?? null,
    impuestoCredDebito: payload.impuestoCredDebito ?? null,
    factorGanancias:    payload.factorGanancias ?? null,
    notas:              payload.notas || null,
    actualizadoPor:     userId || null,
    actualizadoEn:      serverTimestamp(),
  }, { merge: true })
}
